"use client";

import { useEffect, useRef } from "react";

/**
 * Records a visitor session when the middleware detects a new marketing touch.
 *
 * The middleware sets a short-lived, client-readable `tc_tp` flag only when the
 * touch signature actually changes, so this fires a handful of times per
 * visitor rather than on every page view. Without the flag we would either
 * write a Firestore document per pageview or keep `visitor_sessions` empty —
 * which is the state that left every multi-touch journey resolving to null.
 *
 * Sends no payload: identity and attribution are read server-side from HttpOnly
 * cookies, so nothing here is client-forgeable.
 */

const TOUCH_PENDING_COOKIE = "tc_tp";

function hasPendingTouch(): boolean {
    if (typeof document === "undefined") return false;
    return document.cookie
        .split(";")
        .some((entry) => entry.trim().startsWith(`${TOUCH_PENDING_COOKIE}=1`));
}

export function TouchBeacon() {
    const sent = useRef(false);

    useEffect(() => {
        if (sent.current || !hasPendingTouch()) return;
        sent.current = true;

        // keepalive so the request survives a fast navigation away.
        void fetch("/api/track/touch", {
            method: "POST",
            keepalive: true,
        }).catch(() => {
            // Attribution is best-effort; never surface a failure to the shopper.
        });
    }, []);

    return null;
}
